import { keyframes, styled } from '@mui/material/styles';
import {
  OTPInput as HeadlessOTPInput,
  OTPInputProps,
  REGEXP_ONLY_DIGITS,
  REGEXP_ONLY_DIGITS_AND_CHARS,
  SlotProps,
} from 'input-otp';
import React from 'react';

export const OTPInput = styled(OTPInputWrapper, {
  shouldForwardProp: (
    _propName,
  ): _propName is keyof Omit<React.ComponentProps<typeof OTPInputWrapper>, 'className'> => true,
})`
  display: flex;
  align-items: center;
  justify-content: center;
  '&:has(:disabled)': {
    opacity: 0.3;
  };34
`;

const DashContainer = styled('div')({
  display: 'flex',
  width: 40,
  justifyContent: 'center',
  alignItems: 'center',
});

const DashElement = styled('div')(({ theme }) => ({
  width: 12,
  height: 4,
  borderRadius: 9999,
  backgroundColor: theme.palette.divider,
}));

// Keyframes for blinking caret
const caretBlink = keyframes`
  0%,70%,100% { opacity: 1; }
  20%,50% { opacity: 0; }
`;

const CaretContainer = styled('div')({
  position: 'absolute',
  pointerEvents: 'none',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  animation: `${caretBlink} 1.2s ease-out infinite`,
});

const CaretElement = styled('div')(({ theme }) => ({
  width: 1,
  height: 32,
  backgroundColor: theme.palette.text.primary,
}));

const SlotContainer = styled('div')`
  display: flex;
`;

const StyledSlot = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive: boolean }>(({ theme, isActive }) => ({
  position: 'relative',
  width: 40,
  height: 56,
  fontSize: '2rem',

  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  transition: 'all 300ms',

  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderRight: `1px solid ${theme.palette.divider}`,

  // First child border-left and rounded left corners
  '&:first-of-type': {
    borderLeft: `1px solid ${theme.palette.divider}`,
    borderTopLeftRadius: theme.shape.borderRadius,
    borderBottomLeftRadius: theme.shape.borderRadius,
  },

  // Last child rounded right corners
  '&:last-of-type': {
    borderTopRightRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
  },

  outline: '0 solid transparent',
  outlineColor: theme.palette.action.focus,
  outlineWidth: 0,

  // Group hover and focus-within border color change
  // MUI doesn't have group-hover, so you might wrap the parent with a class or use props to simulate
  // For this example, let's assume a class 'group' on the parent and use the sibling selector:
  // This part may need adjustment based on your usage
  '.group:hover & , .group:focus-within &': {
    borderColor: `${theme.palette.primary.main}33`, // 20% opacity equivalent
  },

  ...(isActive && {
    outlineWidth: 4,
    outlineStyle: 'solid',
    outlineColor: theme.palette.primary.main,
  }),
}));

function Slot(props: SlotProps) {
  return (
    <StyledSlot isActive={props.isActive}>
      {props.char !== null && <div>{props.char}</div>}
      {props.hasFakeCaret && <FakeCaret />}
    </StyledSlot>
  );
}

function FakeCaret() {
  return (
    <CaretContainer>
      <CaretElement />
    </CaretContainer>
  );
}

function FakeDash() {
  return (
    <DashContainer>
      <DashElement />
    </DashContainer>
  );
}

const CONFIGS: Record<'otp' | 'backup_code', { length: number; pattern: string }> = {
  otp: {
    length: 6,
    pattern: REGEXP_ONLY_DIGITS,
  },
  backup_code: {
    length: 10,
    pattern: REGEXP_ONLY_DIGITS_AND_CHARS,
  },
};

function OTPInputWrapper({
  className,
  type,
  ...props
}: { className: string; type: 'otp' | 'backup_code' } & Omit<
  OTPInputProps,
  | 'maxLength'
  | 'containerClassName'
  | 'pattern'
  | 'noScriptCSSFallback'
  | 'autoFocus'
  | 'render'
  | 'children'
>) {
  const config = CONFIGS[type];
  return (
    <HeadlessOTPInput
      data-testid={'BoExternalOTPInputWrapper_Input'}
      maxLength={config.length}
      containerClassName={`group ${className}`}
      pattern={config.pattern}
      noScriptCSSFallback={null}
      // oxlint-disable-next-line jsx-a11y/no-autofocus -- OTP input is usually auto focused
      autoFocus
      render={({ slots }) => (
        <>
          <SlotContainer>
            {slots.slice(0, config.length / 2).map((slot, idx) => (
              <Slot key={idx} {...slot} />
            ))}
          </SlotContainer>

          <FakeDash />

          <SlotContainer>
            {slots.slice(config.length / 2).map((slot, idx) => (
              <Slot key={idx} {...slot} />
            ))}
          </SlotContainer>
        </>
      )}
      {...props}
    />
  );
}
